import React, { useMemo, useState } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription } from './ui/dialog';
import { Button } from './ui/button';
import { Label } from './ui/label';
import { Input } from './ui/input';
import { Download, Printer, FileText, CalendarRange } from 'lucide-react';
import { driverEarningsStatementUrl, driverEarningsStatementRangeUrl } from '../services/api';

/**
 * Driver "Download PDF Summary" dialog.
 *
 * Lets the driver pick a range (this week, last week, this month, last month,
 * year-to-date, custom dates) and either Download or Print the resulting PDF.
 *
 * The PDF itself is rendered by the backend's `/api/driver/earnings/statement.pdf`
 * endpoint and always contains: delivery code, route, customer, gross/net/VAT,
 * totals, and (when available) payout status / completion date.
 */
function ymd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

function rangeForPreset(preset) {
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const out = { from: '', to: '' };
  if (preset === 'this_week') {
    const day = today.getDay(); // 0 = Sun
    const monOffset = (day === 0 ? -6 : 1 - day);
    const start = new Date(today);
    start.setDate(today.getDate() + monOffset);
    out.from = ymd(start);
    out.to = ymd(today);
  } else if (preset === 'last_week') {
    const day = today.getDay();
    const monOffset = (day === 0 ? -6 : 1 - day);
    const start = new Date(today);
    start.setDate(today.getDate() + monOffset - 7);
    const end = new Date(start);
    end.setDate(start.getDate() + 6);
    out.from = ymd(start);
    out.to = ymd(end);
  } else if (preset === 'this_month') {
    out.from = ymd(new Date(today.getFullYear(), today.getMonth(), 1));
    out.to = ymd(today);
  } else if (preset === 'last_month') {
    const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    const end = new Date(today.getFullYear(), today.getMonth(), 0);
    out.from = ymd(start);
    out.to = ymd(end);
  } else if (preset === 'ytd') {
    out.from = ymd(new Date(today.getFullYear(), 0, 1));
    out.to = ymd(today);
  }
  return out;
}

export function DownloadStatementDialog({ triggerClassName = '' }) {
  const [open, setOpen] = useState(false);
  const [preset, setPreset] = useState('this_month');
  const initial = useMemo(() => rangeForPreset('this_month'), []);
  const [fromDate, setFromDate] = useState(initial.from);
  const [toDate, setToDate] = useState(initial.to);

  const handlePreset = (p) => {
    setPreset(p);
    if (p !== 'custom') {
      const r = rangeForPreset(p);
      setFromDate(r.from);
      setToDate(r.to);
    }
  };

  const isMonthlyCalendarPreset = () => {
    // Special-case: "last full calendar month" → use the year/month endpoint
    // so the filename / header are nicely labelled "April 2026".
    if (preset !== 'last_month') return null;
    const today = new Date();
    const lm = new Date(today.getFullYear(), today.getMonth() - 1, 1);
    return { year: lm.getFullYear(), month: lm.getMonth() + 1 };
  };

  const buildUrl = () => {
    const mp = isMonthlyCalendarPreset();
    if (mp) return driverEarningsStatementUrl(mp.year, mp.month);
    return driverEarningsStatementRangeUrl(fromDate, toDate);
  };

  const canSubmit = !!fromDate && !!toDate && fromDate <= toDate;

  const handleDownload = () => {
    if (!canSubmit) return;
    const a = document.createElement('a');
    a.href = buildUrl();
    a.download = '';
    a.target = '_blank';
    a.rel = 'noopener noreferrer';
    document.body.appendChild(a);
    a.click();
    a.remove();
  };

  const handlePrint = () => {
    if (!canSubmit) return;
    // Open the PDF in a new tab — modern browsers expose the print dialog
    // from there. Trying window.open + window.print on a cross-origin PDF
    // gets blocked, so we keep it simple: open the PDF and let the user
    // hit ⌘P / Ctrl+P (or use the in-PDF print button).
    const w = window.open(buildUrl(), '_blank', 'noopener,noreferrer');
    if (w) {
      // Best-effort: trigger print once the PDF finishes loading.
      try {
        w.addEventListener('load', () => {
          try { w.focus(); w.print(); } catch { /* cross-origin print blocked */ }
        });
      } catch { /* ignore */ }
    }
  };

  const presets = [
    { key: 'this_week', label: 'This week' },
    { key: 'last_week', label: 'Last week' },
    { key: 'this_month', label: 'This month' },
    { key: 'last_month', label: 'Last month' },
    { key: 'ytd', label: 'Year to date' },
    { key: 'custom', label: 'Custom dates' },
  ];

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className={`gap-1.5 border-[#2E7D32]/30 text-[#2E7D32] hover:bg-[#2E7D32]/5 ${triggerClassName}`}
          data-testid="open-statement-dialog-btn"
        >
          <FileText className="w-4 h-4" /> Download PDF Summary
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md" data-testid="statement-dialog">
        <DialogHeader>
          <DialogTitle className="font-['Inter'] flex items-center gap-2">
            <CalendarRange className="w-5 h-5 text-[#2E7D32]" /> Download earnings summary
          </DialogTitle>
          <DialogDescription className="text-sm">
            Generate a clean PDF of your completed, paid deliveries for any period — perfect for bookkeeping and taxes.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <Label className="text-xs uppercase tracking-wider text-gray-500 font-bold">Period</Label>
            <div className="mt-2 grid grid-cols-3 gap-2">
              {presets.map((p) => (
                <button
                  key={p.key}
                  type="button"
                  onClick={() => handlePreset(p.key)}
                  className={`px-2 py-2 rounded-md text-xs font-semibold border transition-colors ${
                    preset === p.key
                      ? 'bg-[#2E7D32] text-white border-[#1B5E20]'
                      : 'bg-white text-gray-700 border-gray-200 hover:border-[#2E7D32]'
                  }`}
                  data-testid={`statement-preset-${p.key}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="text-xs font-semibold text-gray-700">From</Label>
              <Input
                type="date"
                value={fromDate}
                onChange={(e) => { setFromDate(e.target.value); setPreset('custom'); }}
                max={toDate || undefined}
                className="rounded-sm h-10 mt-1"
                data-testid="statement-from-date"
              />
            </div>
            <div>
              <Label className="text-xs font-semibold text-gray-700">To</Label>
              <Input
                type="date"
                value={toDate}
                onChange={(e) => { setToDate(e.target.value); setPreset('custom'); }}
                min={fromDate || undefined}
                className="rounded-sm h-10 mt-1"
                data-testid="statement-to-date"
              />
            </div>
          </div>

          {!canSubmit && (
            <p className="text-xs text-red-600 font-medium">
              Please pick valid dates (To must be on or after From).
            </p>
          )}

          <div className="rounded-md bg-gray-50 border border-gray-200 p-3 text-xs text-gray-600 leading-relaxed">
            Only completed deliveries with accepted, paid bids are included. The PDF lists every delivery with code, route, customer, amount earned, completion date and full totals. If you're VAT registered, a Net / VAT / Gross breakdown is also shown.
          </div>

          <div className="flex flex-col sm:flex-row gap-2 pt-1">
            <Button
              onClick={handleDownload}
              disabled={!canSubmit}
              className="flex-1 gap-2 bg-[#2E7D32] hover:bg-[#1B5E20] text-white rounded-sm h-11"
              data-testid="statement-download-btn"
            >
              <Download className="w-4 h-4" /> Download PDF
            </Button>
            <Button
              variant="outline"
              onClick={handlePrint}
              disabled={!canSubmit}
              className="flex-1 gap-2 border-gray-300 rounded-sm h-11"
              data-testid="statement-print-btn"
            >
              <Printer className="w-4 h-4" /> Print
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default DownloadStatementDialog;
